import React, { useState, useEffect } from "react";
import { Button, Grid, Box, Typography, InputLabel, Select, FormControl, MenuItem, FormLabel, FormGroup, FormControlLabel, Checkbox, FormHelperText } from "@material-ui/core";
import { useSnackbar } from "notistack";
import { patchSettings, UserSettings, getSettings, getDataExport } from "api/user";
import { Club } from "api/clubs";
import i18n from "./i18n";
import DeleteAccountDialog from "./DeleteAccountDialog";
import PrivacyPolicyDialog from "./PrivacyPolicyDialog";
import TermsOfServiceDialog from "./TermsOfServiceDialog";
import EditContactDataDialog from "./EditContactDataDialog";

// prop types
interface PrivacyTabProps {
    club?: Club | null,
}

// privacy levels
const privacyLevels = ["private", "clubs-only", "public", "public-inviteable"] as const;

// general club settings tab
const PrivacyTab = ({ club }: PrivacyTabProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    // ui state
    const [isLoading, setLoading] = useState(false);
    const [showDeleteAccountDialog, setShowDeleteAccountDialog] = useState(false);
    const [showEditContactDataDialog, setShowEditContactDataDialog] = useState(false);
    const [showPrivacyPolicyDialog, setShowPrivacyPolicyDialog] = useState(false);
    const [showTermsOfServiceDialog, setShowTermsOfServiceDialog] = useState(false);
    const [userSettings, setUserSettings] = useState<UserSettings>();

    const isCompany = club?.capabilities.includes("company");

    // export user data action
    const exportUserDataAction = async () => {
        setLoading(true);
        try {
            const { url } = await getDataExport();
            window.open(url);
        } catch (error) {
            displayError(error);
        }
        setLoading(false);
    };

    // change action
    const saveAction = async (property: keyof UserSettings, value: any) => {
        setLoading(true);
        try {
            await patchSettings({ [property]: value });
            await fetchUserSettings();
            enqueueSnackbar(i18n.savedSuccessfully, { variant: "success" });
        } catch (error) {
            displayError(error);
        }
        setLoading(false);
    };

    // fetch user settings
    const fetchUserSettings = async () => { setUserSettings(await getSettings()); };
    useEffect(() => { fetchUserSettings(); }, []);

    // render
    const SettingsCheckbox = ({ property }: { property: Exclude<keyof UserSettings, "privacyLevel"> }) => (
        <Checkbox
            checked={userSettings && userSettings[property]}
            color="primary"
            disabled={isLoading}
            onChange={(e) => saveAction(property, e.target.checked)}
        />
    );
    return (
        <>
            <Box style={{ alignItems: "center", display: "flex", flexDirection: "column", padding: "16px" }}>
                <Grid container spacing={2} justify="center">

                    {/* notifications */}
                    <Grid item xs={12}>
                        <Typography align="center" variant="subtitle2">{i18n.notifications}</Typography>
                    </Grid>
                    <Grid item xs={8}>
                        <FormControl>
                            <FormLabel>{i18n.email}</FormLabel>
                            <FormGroup>
                                <FormControlLabel
                                    control={<SettingsCheckbox property="followerEmail" />}
                                    label={i18n.newFollower}
                                />
                                <FormControlLabel
                                    control={<SettingsCheckbox property="activityEmail" />}
                                    label={i18n.newStoryOrComment}
                                />
                                <FormControlLabel
                                    control={<SettingsCheckbox property="bookingEmail" />}
                                    label={i18n.newBooking}
                                />
                                <FormControlLabel
                                    control={<SettingsCheckbox property="forYouEmail" />}
                                    label={i18n.forYou}
                                />
                                <FormControlLabel
                                    control={<SettingsCheckbox property="competitionEmail" />}
                                    label={i18n.competitions}
                                />
                                <FormControlLabel
                                    control={<SettingsCheckbox property="isSubscribedToNewsletter" />}
                                    label={i18n.newsletter}
                                />
                            </FormGroup>
                        </FormControl>
                    </Grid>
                    <Grid item xs={8}>
                        <FormControl>
                            <FormLabel>{i18n.push}</FormLabel>
                            <FormGroup>
                                <FormControlLabel
                                    control={<SettingsCheckbox property="followerPushNotification" />}
                                    label={i18n.newFollower}
                                />
                                <FormControlLabel
                                    control={<SettingsCheckbox property="activityPushNotification" />}
                                    label={i18n.newStoryOrComment}
                                />
                                <FormControlLabel
                                    control={<SettingsCheckbox property="bookingPushNotification" />}
                                    label={i18n.newBooking}
                                />
                                <FormControlLabel
                                    control={<SettingsCheckbox property="forYouPushNotification" />}
                                    label={i18n.forYou}
                                />
                                <FormControlLabel
                                    control={<SettingsCheckbox property="competitionPushNotification" />}
                                    label={i18n.competitions}
                                />
                            </FormGroup>
                        </FormControl>
                    </Grid>

                    {/* checkins */}
                    <Grid item xs={12}>
                        <Typography align="center" variant="subtitle2">{i18n.checkIn}</Typography>
                    </Grid>
                    <Grid item xs={8} style={{ display: "flex", justifyContent: "center" }}>
                        <Button variant="contained" onClick={() => setShowEditContactDataDialog(true)}>
                            {i18n.editContactData}
                        </Button>
                    </Grid>

                    {/* account */}
                    <Grid item xs={12} style={{ marginTop: "32px" }}>
                        <Typography align="center" variant="subtitle2">{i18n.account}</Typography>
                    </Grid>
                    <Grid item xs={8}>
                        <FormControl fullWidth variant="outlined">
                            <InputLabel>{i18n.profileVisibility}</InputLabel>
                            <Select
                                label={i18n.profileVisibility}
                                value={userSettings?.privacyLevel || "private"}
                                onChange={(e) => saveAction("privacyLevel", e.target.value)}
                            >
                                {privacyLevels.map((t) => (
                                    <MenuItem key={t} value={t}>{(isCompany ? i18n.profileVisibilitiesForCompany[t] : i18n.profileVisibilities[t])}</MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>{i18n.profileVisibilitiesDesc[userSettings?.privacyLevel || "private"]}</FormHelperText>
                        </FormControl>
                    </Grid>
                    <Grid item xs={8} style={{ display: "flex", justifyContent: "center" }}>
                        <Button variant="contained" onClick={exportUserDataAction} disabled={isLoading}>
                            {i18n.exportAllUserData}
                        </Button>
                    </Grid>
                    <Grid item xs={8} style={{ display: "flex", justifyContent: "center" }}>
                        <Button variant="contained" onClick={() => setShowPrivacyPolicyDialog(true)}>
                            {i18n.privacyPolicy}
                        </Button>
                        <Button variant="contained" style={{ marginLeft: "8px" }} onClick={() => setShowTermsOfServiceDialog(true)}>
                            {i18n.termsOfService}
                        </Button>
                    </Grid>
                    <Grid item xs={8} style={{ display: "flex", justifyContent: "center" }}>
                        <Button
                            style={{ background: "#d44336", color: "#fff" }}
                            variant="contained"
                            onClick={() => setShowDeleteAccountDialog(true)}
                        >
                            {i18n.deleteAccount}
                        </Button>
                    </Grid>

                </Grid>
            </Box>

            {/* dialogs */}
            <DeleteAccountDialog show={showDeleteAccountDialog} onClose={() => setShowDeleteAccountDialog(false)} />
            <EditContactDataDialog show={showEditContactDataDialog} onClose={() => setShowEditContactDataDialog(false)} />
            <PrivacyPolicyDialog show={showPrivacyPolicyDialog} onClose={() => setShowPrivacyPolicyDialog(false)} />
            <TermsOfServiceDialog show={showTermsOfServiceDialog} onClose={() => setShowTermsOfServiceDialog(false)} />
        </>
    );
};

export default PrivacyTab;
